import { FormEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { allowSignedIn, locallySignedOut, markSignedOut, remoteSignOutPending, useSessionTermination } from "./sessionTermination";
import PasswordChangeForm from "./PasswordChangeForm";
import { ConsolePermissionProvider } from "./ConsolePermissions";

type SessionState = {
  configured: boolean;
  authenticated: boolean;
  user?: { id: string; displayName: string };
  permissions?: string[];
  expiresAt?: number;
};

async function sessionRequest(method: "GET" | "POST" | "DELETE", body?: object) {
  const response = await fetch("/api/admin/session", {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    signal: method === "DELETE" ? AbortSignal.timeout(10_000) : undefined
  });
  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || "Named sign-in is unavailable.");
  return payload as SessionState | null;
}

export default function ConsoleSessionBoundary({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionState | null>(null);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recoveryStep, setRecoveryStep] = useState<"signin" | "request" | "verify" | "reset">("signin");
  const [requestId, setRequestId] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [logoutPending, setLogoutPending] = useState(() => remoteSignOutPending("staff"));
  const [notice, setNotice] = useState<string | null>(null);

  const generation = useRef(0);
  const signInHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (session?.authenticated === false) signInHeading.current?.focus(); }, [session?.authenticated]);
  const terminate = useCallback(() => {
    generation.current += 1;
    setLogoutPending(remoteSignOutPending("staff"));
    setSession({ configured: true, authenticated: false });
    setIdentifier(""); setPassword(""); setRequestId(""); setCode(""); setResetToken(""); setNewPassword("");
    setRecoveryStep("signin"); setBusy(false); setError(null); setNotice("Your sign-in has ended. Sign in again.");
  }, []);
  useSessionTermination("staff", session?.authenticated ? session.expiresAt : undefined, terminate);

  useEffect(() => {
    let active = true;
    const current = generation.current;
    sessionRequest("GET")
      .then((next) => { if (active && current === generation.current) setSession(locallySignedOut("staff") ? { configured: next?.configured ?? true, authenticated: false } : next); })
      .catch((reason) => { if (active && current === generation.current) setError(reason instanceof Error ? reason.message : "Named sign-in is unavailable."); })
      .finally(() => { if (active && current === generation.current) setBusy(false); });
    return () => { active = false; };
  }, []);

  async function signIn(event: FormEvent) {
    event.preventDefault();
    if (logoutPending) return;
    setBusy(true);
    setError(null);
    const current = ++generation.current;
    try {
      const next = await sessionRequest("POST", { identifier, password });
      if (current !== generation.current) return;
      if (next?.authenticated) allowSignedIn("staff"); setSession(next);
      setPassword("");
    } catch (reason) {
      if (current === generation.current) setError(reason instanceof Error ? reason.message : "Named sign-in is unavailable.");
    } finally {
      if (current === generation.current) setBusy(false);
    }
  }

  async function signOut() {
    markSignedOut("staff", true);
    terminate(); setLogoutPending(true);
    setError(null);
    setNotice("This page is signed out. Finishing sign out…");
    setBusy(true);
    try {
      await sessionRequest("DELETE");
      markSignedOut("staff"); setLogoutPending(false);
      setNotice("You have signed out.");
    } catch {
      setNotice("This page is signed out. We could not finish signing out of the service. Reconnect before signing in again.");
    } finally {
      setBusy(false);
    }
  }

  async function recover(event: FormEvent) {
    event.preventDefault(); if (logoutPending) return; setBusy(true); setError(null); setNotice(null);
    try {
      if (recoveryStep === "request") {
        const response = await fetch("/api/admin/recovery/requests", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ identifier }) });
        const body = await response.json(); if (!response.ok) throw new Error(body.error); setRequestId(body.requestId); setNotice(body.message); setRecoveryStep("verify");
      } else if (recoveryStep === "verify") {
        const response = await fetch("/api/admin/recovery/verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ requestId, code }) });
        const body = await response.json(); if (!response.ok) throw new Error(body.error); setResetToken(body.resetToken); setCode(""); setRecoveryStep("reset");
      } else {
        const response = await fetch("/api/admin/recovery/reset", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ resetToken, password: newPassword }) });
        if (!response.ok) { const body = await response.json(); throw new Error(body.error); } setNewPassword(""); setResetToken(""); setNotice("Your password has been changed. You can now sign in."); setRecoveryStep("signin");
      }
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Password recovery is unavailable."); }
    finally { setBusy(false); }
  }

  if (busy && !session) return <main className="console-auth-page"><p role="status">Checking staff access…</p></main>;

  if (!session?.authenticated) {
    return <main className="console-auth-page">
      <section className="console-auth-card" aria-labelledby="console-sign-in-title">
        <div className="console-auth-brand"><img src="/nlc-mark.svg" alt="" /><span>North Lanarkshire Council</span></div>
        <p className="console-auth-product">SmartProperties</p>
        <h1 ref={signInHeading} tabIndex={-1} id="console-sign-in-title">Staff sign in</h1>
        <p>Use your named council account to continue.</p>
        {session?.configured ? logoutPending ? <p>Finish signing out before signing in again.</p> : recoveryStep === "signin" ? <form onSubmit={signIn}>
          <label><span>Email or username</span><input autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} /></label>
          <label><span>Password</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
          <button className="console-button" type="submit" disabled={busy || !identifier.trim() || !password}>{busy ? "Signing in…" : "Sign in"}</button>
          <button type="button" onClick={() => { setRecoveryStep("request"); setError(null); setNotice(null); }}>Set up or reset password</button>
        </form> : null : <p>Staff sign in is not available. Ask the support team for help.</p>}
        {session?.configured && !logoutPending && recoveryStep !== "signin" ? <form onSubmit={recover}>
          {recoveryStep === "request" ? <label><span>Email or username</span><input autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} /></label> : null}
          {recoveryStep === "verify" ? <label><span>Six-digit code</span><input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value)} /></label> : null}
          {recoveryStep === "reset" ? <label><span>New password</span><input type="password" autoComplete="new-password" minLength={8} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label> : null}
          <button className="console-button" type="submit" disabled={busy}>{recoveryStep === "request" ? "Send recovery code" : recoveryStep === "verify" ? "Verify code" : "Change password"}</button>
          <button type="button" onClick={() => setRecoveryStep("signin")}>Back to sign in</button>
        </form> : null}
        {notice ? <p role="status">{notice}</p> : null}
        {logoutPending ? <button type="button" disabled={busy} onClick={() => void signOut()}>Finish signing out</button> : null}
        {error ? <p className="console-auth-error" role="alert">{error}</p> : null}
      </section>
    </main>;
  }

  return <>
    <div className="console-session-bar"><span>Signed in as {session.user?.displayName || "named staff user"}</span><button type="button" onClick={() => void signOut()} disabled={busy}>Sign out</button>
      <PasswordChangeForm kind="staff" onEnded={(message, complete) => { markSignedOut("staff", !complete); terminate(); setLogoutPending(!complete); setNotice(message); }} />
    </div>
    <ConsolePermissionProvider permissions={session.permissions ?? []}>{children}</ConsolePermissionProvider>
  </>;
}
